import { StageDef } from '../automationMachineInterfaces'
import { FACEBOOK_AUTOMATION_STAGE_STATUSES } from './facebookAutomationStageStatuses'
import {
  clickRadioButton,
  getNotificationSection,
  getSelectedRadioText,
  getToggleOn,
  setToggle,
} from './fbNotificationUtils'
import { notificationsUrl } from './fbUrls'
import { FacebookSettingKey } from '../../recommendations/recommendationSettingsKeys'
import { FBRecommendationKey } from '../../utils/enums'
import * as enums from '../../utils/enums'
import * as promises from '../interruptiblePromises'
import {
  defaultCantUndoFn,
  getCurrentValueFnFromKey,
  getErrorIsDisabledAndReasonFn,
  getIsFulfilledFnFromKey,
} from '../recommendationCardDisplayUtils'
import { shouldUpdateSetting } from '../utils'

// @TODO 20241030 - "Notifications when someone tags you" (TAGS_THAT_NOTIFY) is
// generating 500 errors from the GraphQL mutation on Facebook's end, even when
// updating the setting manually in the UI. If this setting begins to work
// reliably again, either return this const and conditionals, or set it to
// true. **Also remember to uncomment the setting in facebook.yaml.
const TAGS_THAT_NOTIFY_ENABLED = false

// Setting property functions
const getTagsThatNotifyCurrentValueFn = (key) =>
  getCurrentValueFnFromKey(key, (value) => value || null)
const getTogglesCurrentValueFn = (key) =>
  getCurrentValueFnFromKey(key, (value) =>
    value && value.length > 0 ? value.join(', ') : 'Off',
  )

const currentValueFns = {
  [FacebookSettingKey.COMMENT_NOTIFICATIONS]: getTogglesCurrentValueFn(
    FacebookSettingKey.COMMENT_NOTIFICATIONS,
  ),
  [FacebookSettingKey.ACTIVITY_ABOUT_YOU]: getTogglesCurrentValueFn(
    FacebookSettingKey.ACTIVITY_ABOUT_YOU,
  ),
  [FacebookSettingKey.FRIEND_REQUEST_NOTIFICATIONS]: getTogglesCurrentValueFn(
    FacebookSettingKey.FRIEND_REQUEST_NOTIFICATIONS,
  ),
  [FacebookSettingKey.TAG_NOTIFIFICATIONS]: getTogglesCurrentValueFn(
    FacebookSettingKey.TAG_NOTIFIFICATIONS,
  ),
  [FacebookSettingKey.BATCH_MENTIONS]: getTogglesCurrentValueFn(
    FacebookSettingKey.BATCH_MENTIONS,
  ),
  [FacebookSettingKey.COMMENTED_ON_POSTS]: getTogglesCurrentValueFn(
    FacebookSettingKey.COMMENTED_ON_POSTS,
  ),
  [FacebookSettingKey.JOINED_GROUPS]: getTogglesCurrentValueFn(
    FacebookSettingKey.JOINED_GROUPS,
  ),
  [FacebookSettingKey.MENTIONED_PEOPLE]: getTogglesCurrentValueFn(
    FacebookSettingKey.MENTIONED_PEOPLE,
  ),
  [FacebookSettingKey.TAGS_THAT_NOTIFY]: getTagsThatNotifyCurrentValueFn(
    FacebookSettingKey.TAGS_THAT_NOTIFY,
  ),
}
const getCurrentValueFn = (key) => currentValueFns[key]

const TAGS_THAT_NOTIFY_VALUES = {
  ANYONE: 'Anyone',
  FRIENDS_OF_FRIENDS: 'Friends of Friends',
  FRIENDS: 'Friends',
}

const TAGS_THAT_NOTFY_LEVELS = {
  [TAGS_THAT_NOTIFY_VALUES.ANYONE]: 0,
  [TAGS_THAT_NOTIFY_VALUES.FRIENDS_OF_FRIENDS]: 1,
  [TAGS_THAT_NOTIFY_VALUES.FRIENDS]: 2,
}

const getTagsThatNotifyIsFulfilledFn = (key) =>
  getIsFulfilledFnFromKey(
    key,
    (value) =>
      TAGS_THAT_NOTFY_LEVELS[value] &&
      TAGS_THAT_NOTFY_LEVELS[value] >=
        TAGS_THAT_NOTFY_LEVELS[TAGS_THAT_NOTIFY_VALUES.FRIENDS_OF_FRIENDS],
  )
const getTogglesIsFulfilledFn = (key) =>
  getIsFulfilledFnFromKey(key, (value) => (value ? value.length === 0 : true))

const isFulfilledFns = {
  [FacebookSettingKey.COMMENT_NOTIFICATIONS]: getTogglesIsFulfilledFn(
    FacebookSettingKey.COMMENT_NOTIFICATIONS,
  ),
  [FacebookSettingKey.ACTIVITY_ABOUT_YOU]: getTogglesIsFulfilledFn(
    FacebookSettingKey.ACTIVITY_ABOUT_YOU,
  ),
  [FacebookSettingKey.FRIEND_REQUEST_NOTIFICATIONS]: getTogglesIsFulfilledFn(
    FacebookSettingKey.FRIEND_REQUEST_NOTIFICATIONS,
  ),
  [FacebookSettingKey.TAG_NOTIFIFICATIONS]: getTogglesIsFulfilledFn(
    FacebookSettingKey.TAG_NOTIFIFICATIONS,
  ),
  [FacebookSettingKey.BATCH_MENTIONS]: getTogglesIsFulfilledFn(
    FacebookSettingKey.BATCH_MENTIONS,
  ),
  [FacebookSettingKey.COMMENTED_ON_POSTS]: getTogglesIsFulfilledFn(
    FacebookSettingKey.COMMENTED_ON_POSTS,
  ),
  [FacebookSettingKey.JOINED_GROUPS]: getTogglesIsFulfilledFn(
    FacebookSettingKey.JOINED_GROUPS,
  ),
  [FacebookSettingKey.MENTIONED_PEOPLE]: getTogglesIsFulfilledFn(
    FacebookSettingKey.MENTIONED_PEOPLE,
  ),
  [FacebookSettingKey.TAGS_THAT_NOTIFY]: getTagsThatNotifyIsFulfilledFn(
    FacebookSettingKey.TAGS_THAT_NOTIFY,
  ),
}
const getIsFulfilledFn = (key) => isFulfilledFns[key]

const getCantUndoFn = () => defaultCantUndoFn
function getIsDisabledFn(settingsKey: string) {
  const fn = getErrorIsDisabledAndReasonFn(settingsKey)
  return (automationErrors) => fn(automationErrors).isDisabled
}
function getIsDisabledReasonFn(settingsKey: string) {
  const fn = getErrorIsDisabledAndReasonFn(settingsKey)
  return (automationErrors) => fn(automationErrors).isDisabledReason
}

const notificationToggleRowLabels: Partial<Record<FacebookSettingKey, string>> =
  {
    [FacebookSettingKey.COMMENT_NOTIFICATIONS]: 'Comments',
    [FacebookSettingKey.ACTIVITY_ABOUT_YOU]: 'More Activity About You',
    [FacebookSettingKey.FRIEND_REQUEST_NOTIFICATIONS]: 'Friend Requests',
  }

const notificationToggleSettingLabels: Partial<
  Record<FacebookSettingKey, string[]>
> = {
  [FacebookSettingKey.COMMENT_NOTIFICATIONS]: ['Push', 'Email', 'SMS'],
  [FacebookSettingKey.ACTIVITY_ABOUT_YOU]: ['Allow notifications on Facebook'],
  [FacebookSettingKey.FRIEND_REQUEST_NOTIFICATIONS]: ['Push', 'Email', 'SMS'],
}

const notificationTaggingToggleSettingLabels: Partial<
  Record<FacebookSettingKey, string[]>
> = {
  [FacebookSettingKey.TAG_NOTIFIFICATIONS]: ['Push', 'Email', 'SMS'],
  [FacebookSettingKey.BATCH_MENTIONS]: ['Batch mentions'],
  [FacebookSettingKey.COMMENTED_ON_POSTS]: ['Posts you commented on before'],
  [FacebookSettingKey.JOINED_GROUPS]: ['Joined groups'],
  [FacebookSettingKey.MENTIONED_PEOPLE]: ['People you mentioned before'],
}

function shouldUpdateTagsThatNotify(settingsSelected, initialValues): boolean {
  return (
    settingsSelected[FacebookSettingKey.TAGS_THAT_NOTIFY] &&
    initialValues[FacebookSettingKey.TAGS_THAT_NOTIFY] &&
    !getIsFulfilledFn(FacebookSettingKey.TAGS_THAT_NOTIFY)(initialValues)
  )
}

// Gather stage

enum StageKey {
  GATHER_SETTING_USER_NOTIFICATIONS_TOGGLES = 'GATHER_SETTING_USER_NOTIFICATIONS_TOGGLES',
  GATHER_SETTING_USER_NOTIFICATIONS_TAGGING = 'GATHER_SETTING_USER_NOTIFICATIONS_TAGGING',
  UPDATE_SETTING_USER_NOTIFICATIONS_TOGGLES = 'UPDATE_SETTING_USER_NOTIFICATIONS_TOGGLES',
  UPDATE_SETTING_USER_NOTIFICATIONS_TAGGING = 'UPDATE_SETTING_USER_NOTIFICATIONS_TAGGING',
  UNDO_SETTING_USER_NOTIFICATIONS_TOGGLES = 'UNDO_SETTING_USER_NOTIFICATIONS_TOGGLES',
  UNDO_SETTING_USER_NOTIFICATIONS_TAGGING = 'UNDO_SETTING_USER_NOTIFICATIONS_TAGGING',
}

const gatherStageDefs: StageDef[] = [
  {
    type: enums.StageType.ACTION,
    key: StageKey.GATHER_SETTING_USER_NOTIFICATIONS_TOGGLES,
    status: FACEBOOK_AUTOMATION_STAGE_STATUSES.GATHERING_SETTINGS,
    url: notificationsUrl,
    action: async (machineContext, userContext) => {
      const { updateStoredAutomationErrors, updateStoredInitialValues } =
        userContext

      const automationErrors = {}

      for (const settingKey of Object.keys(notificationToggleRowLabels)) {
        try {
          const section = await getNotificationSection(
            notificationToggleRowLabels[settingKey],
          )
          if (section && section.ariaExpanded !== 'true') {
            section.click()
            await promises.wait(500)
          }

          const initialValuesOn = []
          for (const settingLabel of notificationToggleSettingLabels[
            settingKey
          ]) {
            const toggleValue = await getToggleOn(
              notificationToggleRowLabels[settingKey],
              settingLabel,
            )
            if (toggleValue) {
              initialValuesOn.push(settingLabel)
            }
          }

          await updateStoredInitialValues(
            {
              [settingKey]: initialValuesOn,
            },
            FBRecommendationKey.FB_USER_ACTIVITY_NOTIFICATIONS,
          )
        } catch (error) {
          automationErrors[settingKey] = {
            gatherError: { message: error.message, name: error.name },
          }

          await updateStoredAutomationErrors(
            automationErrors,
            FBRecommendationKey.FB_USER_ACTIVITY_NOTIFICATIONS,
          )
        }
      }
    },
  },
  {
    type: enums.StageType.ACTION,
    key: StageKey.GATHER_SETTING_USER_NOTIFICATIONS_TAGGING,
    status: FACEBOOK_AUTOMATION_STAGE_STATUSES.GATHERING_SETTINGS,
    url: notificationsUrl,
    action: async (machineContext, userContext) => {
      const { updateStoredAutomationErrors, updateStoredInitialValues } =
        userContext

      const automationErrors = {}

      const section = await getNotificationSection('Tags')
      if (section && section.ariaExpanded !== 'true') {
        section.click()
        await promises.wait(500)
      }

      // Gather tags that notify radio button
      if (TAGS_THAT_NOTIFY_ENABLED) {
        try {
          const radioLabel = await getSelectedRadioText('Tags', () => true)
          await updateStoredInitialValues(
            {
              [FacebookSettingKey.TAGS_THAT_NOTIFY]: radioLabel,
            },
            FBRecommendationKey.FB_USER_ACTIVITY_NOTIFICATIONS,
          )
        } catch (error) {
          automationErrors[FacebookSettingKey.TAGS_THAT_NOTIFY] = {
            gatherError: { message: error.message, name: error.name },
          }
          await updateStoredAutomationErrors(
            automationErrors,
            FBRecommendationKey.FB_USER_ACTIVITY_NOTIFICATIONS,
          )
        }
      }

      // Tagging Toggles (may be more than 1 per setting)
      for (const settingKey of Object.keys(
        notificationTaggingToggleSettingLabels,
      )) {
        try {
          const initialValuesOn = []

          for (const settingLabel of notificationTaggingToggleSettingLabels[
            settingKey
          ]) {
            const toggleValue = await getToggleOn('Tags', settingLabel)
            if (toggleValue) {
              initialValuesOn.push(settingLabel)
            }
          }

          await updateStoredInitialValues(
            {
              [settingKey]: initialValuesOn,
            },
            FBRecommendationKey.FB_USER_ACTIVITY_NOTIFICATIONS,
          )
        } catch (error) {
          automationErrors[settingKey] = {
            gatherError: { message: error.message, name: error.name },
          }
          await updateStoredAutomationErrors(
            automationErrors,
            FBRecommendationKey.FB_USER_ACTIVITY_NOTIFICATIONS,
          )
        }
      }
    },
  },
]

const updateStageDefs: StageDef[] = [
  {
    type: enums.StageType.ACTION,
    key: StageKey.UPDATE_SETTING_USER_NOTIFICATIONS_TOGGLES,
    status: FACEBOOK_AUTOMATION_STAGE_STATUSES.UPDATING_SETTINGS,
    url: notificationsUrl,
    action: async (machineContext, userContext) => {
      const { initialValues, isDry, settingsSelected } = userContext

      if (!settingsSelected) {
        return null
      }

      for (const settingKey of Object.keys(notificationToggleRowLabels)) {
        if (
          shouldUpdateSetting(
            settingsSelected,
            initialValues,
            settingKey,
            getIsFulfilledFn,
          ) &&
          !isDry
        ) {
          const section = await getNotificationSection(
            notificationToggleRowLabels[settingKey],
          )
          if (section && section.ariaExpanded !== 'true') {
            section.click()
            await promises.wait(500)
          }

          for (const settingLabel of notificationToggleSettingLabels[
            settingKey
          ]) {
            await setToggle(
              notificationToggleRowLabels[settingKey],
              settingLabel,
              false,
            )
            await promises.wait(500)
          }
        }
      }

      return true
    },
  },
  {
    type: enums.StageType.ACTION,
    key: StageKey.UPDATE_SETTING_USER_NOTIFICATIONS_TAGGING,
    status: FACEBOOK_AUTOMATION_STAGE_STATUSES.UPDATING_SETTINGS,
    url: notificationsUrl,
    action: async (machineContext, userContext) => {
      const { settingsSelected, initialValues, isDry } = userContext

      if (!settingsSelected) {
        return null
      }

      const section = await getNotificationSection('Tags')
      if (section && section.ariaExpanded !== 'true') {
        section.click()
        await promises.wait(500)
      }

      if (TAGS_THAT_NOTIFY_ENABLED) {
        if (
          shouldUpdateTagsThatNotify(settingsSelected, initialValues) &&
          !isDry
        ) {
          await clickRadioButton(
            'Tags',
            TAGS_THAT_NOTIFY_VALUES.FRIENDS_OF_FRIENDS,
          )
        }
      }

      for (const settingKey of Object.keys(
        notificationTaggingToggleSettingLabels,
      )) {
        if (
          shouldUpdateSetting(
            settingsSelected,
            initialValues,
            settingKey,
            getIsFulfilledFn,
          ) &&
          !isDry
        ) {
          for (const settingLabel of notificationTaggingToggleSettingLabels[
            settingKey
          ]) {
            await setToggle('Tags', settingLabel, false)
            await promises.wait(500)
          }
        }
      }
      return true
    },
  },
]

const undoStageDefs: StageDef[] = [
  {
    type: enums.StageType.ACTION,
    key: StageKey.UNDO_SETTING_USER_NOTIFICATIONS_TOGGLES,
    status: FACEBOOK_AUTOMATION_STAGE_STATUSES.UNDOING_SETTINGS,
    url: notificationsUrl,
    action: async (machineContext, userContext) => {
      const { settingsSelected, initialValues, isDry } = userContext
      if (!settingsSelected || !initialValues) {
        return null
      }

      for (const settingKey of Object.keys(notificationToggleRowLabels)) {
        if (
          settingsSelected[settingKey] &&
          initialValues[settingKey] &&
          !isDry
        ) {
          const section = await getNotificationSection(
            notificationToggleRowLabels[settingKey],
          )
          if (section && section.ariaExpanded !== 'true') {
            section.click()
            await promises.wait(500)
          }

          for (const settingLabel of initialValues[settingKey]) {
            await setToggle(
              notificationToggleRowLabels[settingKey],
              settingLabel,
              true,
            )
            await promises.wait(500)
          }
        }
      }

      return true
    },
  },
  {
    type: enums.StageType.ACTION,
    key: StageKey.UNDO_SETTING_USER_NOTIFICATIONS_TAGGING,
    status: FACEBOOK_AUTOMATION_STAGE_STATUSES.UNDOING_SETTINGS,
    url: notificationsUrl,
    action: async (machineContext, userContext) => {
      const { settingsSelected, initialValues, isDry } = userContext
      if (!settingsSelected || !initialValues) {
        return null
      }

      const section = await getNotificationSection('Tags')
      if (section && section.ariaExpanded !== 'true') {
        section.click()
        await promises.wait(500)
      }

      if (TAGS_THAT_NOTIFY_ENABLED) {
        if (initialValues[FacebookSettingKey.TAGS_THAT_NOTIFY] && !isDry) {
          await clickRadioButton(
            'Tags',
            initialValues[FacebookSettingKey.TAGS_THAT_NOTIFY],
          )
        }
      }

      for (const settingKey of Object.keys(
        notificationTaggingToggleSettingLabels,
      )) {
        if (
          settingsSelected[settingKey] &&
          initialValues[settingKey] &&
          !isDry
        ) {
          for (const settingLabel of initialValues[settingKey]) {
            await setToggle('Tags', settingLabel, true)
            await promises.wait(500)
          }
        }
      }
      return true
    },
  },
]

export {
  getCantUndoFn,
  getCurrentValueFn,
  getIsDisabledFn,
  getIsDisabledReasonFn,
  getIsFulfilledFn,
  gatherStageDefs,
  updateStageDefs,
  undoStageDefs,
}
